import { useCallback } from 'react'

import { useChainId } from 'wagmi'

import { GasParams, Hash } from 'types/entities'

import { usePrivateKey, useSettingRpcUrl } from 'state/user/hooks'

import { parseErrorToUserReadableMessage } from 'utils/errors'

import { errors } from 'constants/errors'
import { etherNumbers } from 'constants/variables'

import { usePrivateKeyWallet } from './usePrivateKeyWallet'

export type CreatePrivateKeyTransactionParams = {
  to: Hash
  gas?: bigint
  nonce?: number
  calldata: Hash
  amount?: bigint
  gasParams: GasParams
}

export function useCreateTransactionByPrivateKey() {
  const chainId = useChainId()
  const rpcUrl = useSettingRpcUrl()
  const privateKey = usePrivateKey()

  const getPrivateKeyWallet = usePrivateKeyWallet()

  return useCallback(
    async ({
      to,
      gas,
      nonce,
      calldata,
      gasParams,
      amount = etherNumbers.BG_ZERO,
    }: CreatePrivateKeyTransactionParams): Promise<Hash> => {
      try {
        if (!privateKey) {
          throw new Error(errors.validation.PK_IS_REQUIRED)
        }

        const wallet = getPrivateKeyWallet(privateKey)
        const txHash = await wallet.sendTransaction({
          to,
          gas,
          nonce,
          value: amount,
          data: calldata,
          ...gasParams,
        })
        return txHash
      } catch (err) {
        throw new Error(parseErrorToUserReadableMessage(err))
      }
    },
    [chainId, rpcUrl, getPrivateKeyWallet],
  )
}
